import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { ArrowLeft, Pencil, Database, Lightbulb, Maximize, Minimize, Maximize2, X, RefreshCw, Lock, Share2, ChevronDown, Copy } from 'lucide-react';
import { apiFetch } from '../../api';
import { useAuth } from '../../context/useAuth';
import { WidgetRenderer } from '../../components/DashboardStudio/WidgetRenderer';
import { useNotification } from '../../context/NotificationContext';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import {
    DropdownMenu as ViewDropdownMenu,
    DropdownMenuContent as ViewDropdownMenuContent,
    DropdownMenuItem as ViewDropdownMenuItem,
    DropdownMenuTrigger as ViewDropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { resolveColorTheme, getWidgetLayoutStyle, type ColorThemeId, type LayoutId } from '../../lib/dashboardThemes';
import { isTenantScoped } from '../../utils/permissions';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { DashboardShareSheet } from '@/components/DashboardStudio/DashboardShareSheet';

const ResponsiveGridLayout = WidthProvider(Responsive);

const CHART_TYPE_LABELS: Record<string, string> = {
    bar: 'Bar', line: 'Line', area: 'Area', pie: 'Pie', donut: 'Donut',
    scatter: 'Scatter', bubble: 'Bubble', kpi: 'KPI', table: 'Table',
    combo: 'Combo', heatmap: 'Heatmap', sankey: 'Sankey', funnel: 'Funnel',
    treemap: 'Treemap', gauge: 'Gauge', sparkline: 'Sparkline',
    waterfall: 'Waterfall', boxplot: 'Box Plot', filter: 'Filter',
    text: 'Text', pageHeader: 'Header',
};

/**
 * Optimized View Mode for Dashboards
 * - Lightweight: No dataset dropdown, no toolbox, no editing
 * - Fast: Loads only datasets used by widgets
 * - Read-only: Pure data visualization
 * - Responsive: Matches Editor layout accurately
 */
interface DashboardViewPageProps {
    dashboardId?: string;
    onBack?: () => void;
    onEdit?: (id?: string) => void;
    embedded?: boolean;
    studioFlow?: boolean;
    readOnly?: boolean;
    /** When provided, fullscreen is controlled externally (e.g. TenantAppRunnerPage). */
    externalFullScreen?: boolean;
    onToggleFullScreen?: () => void;
}

const DashboardViewPage = ({ dashboardId: propId, onBack, onEdit, embedded = false, studioFlow = false, externalFullScreen, onToggleFullScreen }: DashboardViewPageProps) => {
    const params = useParams();
    const dashboardId = propId || params.id;
    const navigate = useNavigate();
    const location = useLocation();
    const { token, user } = useAuth();
    const { addNotification } = useNotification();

    // State
    const [dashboard, setDashboard] = useState<any>(null);
    const [widgetItems, setWidgetItems] = useState<any[]>([]);
    const [widgetDatasets, setWidgetDatasets] = useState<Record<string, any>>({});
    const [selectedDataModel, setSelectedDataModel] = useState<any>(null);
    const [customMeasures, setCustomMeasures] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeInsightWidgetId, setActiveInsightWidgetId] = useState<string | null>(null);
    const [globalFilters, setGlobalFilters] = useState<Record<string, any>>({});
    const [_internalFullScreen, _setInternalFullScreen] = useState(false);
    // If external control is provided (e.g. embedded in TenantAppRunnerPage), use the parent's state.
    const isFullScreen = externalFullScreen !== undefined ? externalFullScreen : _internalFullScreen;
    const [expandedWidgetId, setExpandedWidgetId] = useState<string | null>(null);
    const [isExportSnapshot, setIsExportSnapshot] = useState(false); // true only during phase-2 (isSnapshot)
    const [showEditConfirm, setShowEditConfirm] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [colorThemeId, setColorThemeId] = useState<ColorThemeId>('light');
    const [layoutId, setLayoutId] = useState<LayoutId>('flat');
    const [sharedWith, setSharedWith] = useState<string[]>([]);
    const [publishShareMode, setPublishShareMode] = useState<'me' | 'all'>('me');
    const [shareUserCandidates, setShareUserCandidates] = useState<Array<{ id: string; name?: string; email?: string }>>([]);
    const colorTheme = useMemo(() => resolveColorTheme(colorThemeId), [colorThemeId]);
    const dashboardOwnerId = dashboard?.created_by ?? dashboard?.createdBy ?? '';
    const isPublished = ((dashboard?.status || '') as string).toLowerCase() === 'published';
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isUnlockingForEdit, setIsUnlockingForEdit] = useState(false);
    const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);
    const { shareToken, shareKeyVersion } = useMemo(() => {
        const sp = new URLSearchParams(location.search);
        const st = sp.get('st') || sp.get('token');
        const kv = sp.get('kv');
        return { shareToken: st || null, shareKeyVersion: kv };
    }, [location.search]);
    // Owner check must accept API camelCase (`createdBy`) and snake_case (`created_by`).
    const isOwner =
        !!user?.id &&
        !!dashboardOwnerId &&
        String(user.id) === String(dashboardOwnerId);
    // Draft: direct edit. Published: still allow owners to unlock (unpublish) from studio — see handleEdit / proceedToEdit.
    const canShowEditButton = isOwner && !shareToken && !isPublished;
    /** Studio published bar: must not use `canShowEditButton` alone or the unlock control disappears while "Published · View Only" is shown. */
    const canShowStudioHeaderEdit = isOwner && !shareToken;

    // Escape key handler to close expanded widget
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && expandedWidgetId) {
                setExpandedWidgetId(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [expandedWidgetId]);

    // Phase 1: data source badge
    const [dataSourceStatus, setDataSourceStatus] = useState<{ source: 'platform' | 'tenant'; status: string; last_refreshed_at: string | null } | null>(null);
    // Gate: block chart queries until auto-selected filters are resolved
    const [filtersReady, setFiltersReady] = useState(false);
    const pendingLatestRef = React.useRef(0);
    // Guard: track which dashboardId has already been recorded as viewed.
    // The loadDashboard effect can re-run many times (refreshKey, token change, etc.)
    // but we must only fire POST /viewed ONCE per dashboard ID.
    const viewedDashboardRef = React.useRef<string | null>(null);

    // Full Screen Toggle Logic (CSS Overlay)
    const toggleFullScreen = () => {
        if (onToggleFullScreen) {
            onToggleFullScreen(); // Let parent control it
        } else {
            _setInternalFullScreen(v => !v);
        }
    };

    // Keep legacy top-hover control behavior outside Dashboard Studio flow.
    const [showControls, setShowControls] = useState(true);
    const controlsTimeoutRef = React.useRef<any>(null);
    const handleMouseMove = (e: React.MouseEvent) => {
        if (studioFlow || !isFullScreen) return;
        if (e.clientY < 80) {
            setShowControls(true);
            if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
            controlsTimeoutRef.current = setTimeout(() => {
                setShowControls(false);
            }, 2500);
        }
    };
    useEffect(() => {
        if (studioFlow) return;
        if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        if (isFullScreen) {
            setShowControls(true);
            controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 2000);
        } else {
            setShowControls(true);
        }
    }, [isFullScreen, studioFlow]);
    useEffect(() => {
        return () => {
            if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        };
    }, []);

    const handleFilterChange = (field: string, value: any) => {
        setGlobalFilters(prev => {
            const newFilters = { ...prev };
            if (value === null || value === undefined || value === '') {
                delete newFilters[field];
            } else {
                newFilters[field] = value;
            }
            return newFilters;
        });
    };

    // Called by FilterWidget when a useLatest auto-selection fires
    const handleFiltersResolved = () => {
        pendingLatestRef.current = Math.max(0, pendingLatestRef.current - 1);
        if (pendingLatestRef.current === 0) {
            setFiltersReady(true);
        }
    };

    // DEBUG LOGS
    // console.log('[DashboardViewPage] MOUNTED. ID:', dashboardId, '{Prop:', propId, 'Params:', params.id, '}');

    // Handle Back and Edit
    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate('/dashboards');
        }
    };

    const proceedToEdit = async () => {
        if (shareToken || !isOwner) return;
        try {
            setIsUnlockingForEdit(true);
            if (studioFlow && (dashboard?.status || '').toLowerCase() === 'published') {
                await apiFetch(`/dashboards/${dashboardId}/unpublish`, token, { method: 'POST' });
            }
            if (onEdit) {
                onEdit(dashboardId); // Pass ID to callback
            } else {
                navigate(`/dashboards/${dashboardId}/edit`);
            }
        } catch (err: any) {
            setError(err?.message || 'Failed to open dashboard editor');
        } finally {
            setIsUnlockingForEdit(false);
        }
    };

    const handleEdit = () => {
        if (shareToken || !isOwner) return;
        proceedToEdit();
    };

    const handleRefresh = () => {
        if (isRefreshing || loading) return;
        setIsRefreshing(true);
        setRefreshKey((k) => k + 1);
    };

    // Full data pull: POST /datamodels/{id}/refresh → poll until done → reload widgets
    const [isDataPullRefreshing, setIsDataPullRefreshing] = useState(false);
    const handleRefreshWithDataPull = async () => {
        if (isDataPullRefreshing || isRefreshing || loading) return;
        const dataModelId = dashboard?.config?.dataModelId
            || dashboard?.theme_config?.dataModelId;
        if (!dataModelId || !token) {
            // No data model — fall back to widget reload
            handleRefresh();
            return;
        }
        setIsDataPullRefreshing(true);
        addNotification(
            '🔄 Data model refresh initiated — this may take a few minutes. Your dashboard will update automatically when done.',
            'warning',
            8000
        );
        try {
            await apiFetch(`/datamodels/${dataModelId}/refresh`, token, { method: 'POST' });
        } catch (e: any) {
            console.warn('[DashboardViewPage] Data pull trigger failed:', e?.message);
            addNotification('Could not start data pull: ' + (e?.message || 'Unknown error'), 'error');
            setIsDataPullRefreshing(false);
            return;
        }
        // Notify the Data Models page (if open) so it shows the "Refreshing…" badge immediately
        window.dispatchEvent(new CustomEvent('datamodel-refresh-started', { detail: { modelId: dataModelId } }));
        // Poll until job finishes (max 5 min)
        const POLL_MS = 3000;
        const MAX_MS = 5 * 60 * 1000;
        const t0 = Date.now();
        let done = false;
        let finalStatus = '';
        while (!done && Date.now() - t0 < MAX_MS) {
            await new Promise<void>((r) => setTimeout(r, POLL_MS));
            try {
                const status = await apiFetch(`/datamodels/${dataModelId}/refresh-status`, token);
                finalStatus = (status?.refresh_status || '').toUpperCase();
                if (finalStatus !== 'RUNNING' && finalStatus !== 'QUEUED' && finalStatus !== 'PENDING') done = true;
            } catch { done = true; }
        }
        setIsDataPullRefreshing(false);
        if (!done) {
            addNotification('Data refresh is still running in the background. Check the Data Models page for status.', 'warning');
        } else if (finalStatus === 'FAILED') {
            addNotification('Data refresh failed — showing last available data.', 'error');
        } else {
            addNotification('✅ Data refreshed successfully — dashboard updated with latest data.', 'success');
        }
        // Reload widget queries against the freshly-written parquet
        setIsRefreshing(true);
        setRefreshKey((k) => k + 1);
    };

    const sanitizeFilePart = (value: string) =>
        (value || 'dashboard').replace(/[^a-z0-9]/gi, '_').toLowerCase();

    const exportSnapshotPdf = async () => {
        if (isExportingPdf) return;
        const dashboardElement = document.getElementById('dashboard-view-canvas');
        if (!dashboardElement) return;

        try {
            const DATA_TIMEOUT_MS = 30000;
            const SNAP_TIMEOUT_MS = 10000;
            const POLL_MS = 200;

            // ── Phase 1: wait for widget data WITHOUT enabling snapshot mode ──────────
            // isExportSnapshot stays false → WidgetRenderer keeps live ECharts canvases.
            // We only wait until all API calls and loaders are gone.
            setIsExportingPdf(true);

            const dataStart = Date.now();
            while (Date.now() - dataStart < DATA_TIMEOUT_MS) {
                const loadingWidgets = dashboardElement.querySelectorAll('[data-widget-loading="true"]').length;
                const loadingSpinners = dashboardElement.querySelectorAll('.animate-spin').length;
                const pendingApiCalls = Number((window as any).__widgetApiPending || 0);
                if (loadingWidgets === 0 && loadingSpinners === 0 && pendingApiCalls === 0) break;
                await new Promise<void>((resolve) => setTimeout(resolve, POLL_MS));
            }

            // Extra settle so ECharts finishes its entrance animations.
            await new Promise<void>((resolve) => setTimeout(resolve, 800));

            // ── Phase 2: enable snapshot mode → WidgetRenderer calls getDataURL() ────
            // Charts are fully rendered now; the captured image will be correct.
            setIsExportSnapshot(true);

            // Let React re-render with isSnapshot=true on all WidgetRenderers.
            await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

            // Wait until every ECharts widget reports snapshot-ready="true".
            const snapStart = Date.now();
            while (Date.now() - snapStart < SNAP_TIMEOUT_MS) {
                const pendingSnapshots = dashboardElement.querySelectorAll('[data-snapshot-ready="false"]').length;
                if (pendingSnapshots === 0) break;
                await new Promise<void>((resolve) => setTimeout(resolve, POLL_MS));
            }

            // Final paint-settle before capture.
            await new Promise<void>((resolve) => setTimeout(resolve, 300));

            // ── Phase 3: capture ─────────────────────────────────────────────────────
            const canvas = await html2canvas(dashboardElement, {
                scale: 2,
                useCORS: true,
                logging: false,
                allowTaint: true,
                backgroundColor: '#f8fafc',
                ignoreElements: (el) =>
                    el.classList.contains('no-snapshot') ||
                    el.classList.contains('react-resizable-handle'),
            });

            const imageData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
            const drawWidth = canvas.width * ratio;
            const drawHeight = canvas.height * ratio;
            const x = (pageWidth - drawWidth) / 2;
            const y = 24;

            pdf.addImage(imageData, 'PNG', x, y, drawWidth, drawHeight, undefined, 'FAST');
            pdf.save(`${sanitizeFilePart(dashboard?.name || 'dashboard')}_snapshot.pdf`);
        } finally {
            setIsExportingPdf(false);
            setIsExportSnapshot(false);
        }
    };

    const applyShareSettings = async ({
        audience,
        subjectIds,
        expiresAt,
    }: {
        audience: 'organization' | 'existing_access' | 'specific_people' | 'tenant_admins';
        subjectIds: string[];
        expiresAt: string | null;
    }) => {
        if (!dashboardId) throw new Error('Dashboard ID is missing.');

        const res = await apiFetch(`/dashboards/${dashboardId}/share-link`, token, {
            method: 'POST',
            body: JSON.stringify({
                audience,
                subjectIds,
                expiresAt,
                publishShareMode,
            }),
        });

        const nextSharedWith = Array.isArray(res?.sharedWith) ? res.sharedWith : [];
        setSharedWith(nextSharedWith);
        setPublishShareMode(nextSharedWith.includes('*') ? 'all' : 'me');

        const relativeUrl = res?.relativeUrl;
        if (!relativeUrl || !String(relativeUrl).includes('st=') || !String(relativeUrl).includes('kv=')) {
            throw new Error('Failed to generate secure share link. Please try again.');
        }

        addNotification('Link settings updated', 'success');
        return `${window.location.origin}${relativeUrl}`;
    };

    // === Load Dashboard ===
    useEffect(() => {
        const controller = new AbortController();
        setIsRefreshing(false);

        const loadDashboard = async () => {
            try {
                setLoading(true);
                // 1. Fetch Dashboard Metadata
                const shareQs = new URLSearchParams();
                if (shareToken) shareQs.set('st', shareToken);
                if (shareKeyVersion != null && shareKeyVersion !== '') shareQs.set('kv', shareKeyVersion);
                const endpoint = shareToken
                    ? `/dashboards/${dashboardId}?${shareQs.toString()}`
                    : `/dashboards/${dashboardId}`;
                const dash = await apiFetch(endpoint, token, { signal: controller.signal });

                if (!dash) {
                    if (!controller.signal.aborted) setError('Dashboard not found');
                    return;
                }

                if (controller.signal.aborted) return;
                setDashboard(dash);
                const rawShared = dash.shared_with ?? dash.sharedWith ?? [];
                const normalizedShared = Array.isArray(rawShared) ? rawShared.map((v: any) => String(v)) : [];
                setSharedWith(normalizedShared);
                const nextPublishShareMode = (dash.publish_share_mode ?? dash.publishShareMode)
                    || (normalizedShared.includes('*') ? 'all' : 'me');
                setPublishShareMode(nextPublishShareMode === 'all' ? 'all' : 'me');

                const loadedConfig = dash.theme_config || dash.config || {};
                setColorThemeId(resolveColorTheme(loadedConfig.colorTheme || loadedConfig.theme || 'light').id as ColorThemeId);
                setLayoutId((loadedConfig.layout || 'flat') as LayoutId);

                // Parse widgets
                const widgets = Object.entries(dash.widgets || {}).map(([id, config]: [string, any]) => {
                    const effectiveModelId = config.data_model_id || config.dataModelId;
                    return {
                        id,
                        type: config.type || 'bar',
                        title: config.title,
                        dataModelId: effectiveModelId,
                        dashboardId: dashboardId, // Pass ID for widget context
                        config: config.config || {},
                        layout: config.layout || dash.layout_config?.find((l: any) => l.i === id)
                    };
                });

                setWidgetItems(widgets);

                // --- INITIALIZE DEFAULT FILTERS ---
                const initialFilters: Record<string, any> = {};
                widgets.forEach(w => {
                    if (w.type === 'filter' && w.config?.column) {
                        w.config.column.forEach((col: any) => {
                            if (col.defaultValue !== undefined && col.defaultValue !== null && col.defaultValue !== '') {
                                initialFilters[col.name] = col.defaultValue;
                            }
                        });
                    }
                });
                if (Object.keys(initialFilters).length > 0) {
                    // console.log('[DashboardViewPage] Initializing Default Filters:', initialFilters);
                    setGlobalFilters(initialFilters);
                }

                // Count how many filter fields have useLatest=true (require async resolution)
                let latestCount = 0;
                widgets.forEach(w => {
                    if (w.type === 'filter' && w.config?.column) {
                        w.config.column.forEach((col: any) => {
                            if (col.useLatest) latestCount++;
                        });
                    }
                });
                pendingLatestRef.current = latestCount;
                // If no useLatest filters, charts can load immediately
                setFiltersReady(latestCount === 0);

                // OPTIMIZATION: Removed Metadata Waterfall
                // We don't need to fetch full dataset metadata for View Mode.
                // The widgets only need the ID to query the backend.

                // Build widgetId -> dataset map (Mock object with ID)
                const datasetsMap = {};
                widgets.forEach(w => {
                    if (w.dataModelId) {
                        datasetsMap[w.id] = { id: w.dataModelId };
                    }
                });

                // console.log('[DashboardViewPage] Data Loaded (Fast Mode). Widgets:', widgets.length);

                setWidgetDatasets(datasetsMap);

                // --- LOAD DATA MODEL + CUSTOM MEASURES ---
                const loadedMeasures = dash.customMeasures || dash.custom_measures || loadedConfig.customMeasures;
                if (loadedMeasures && loadedMeasures.length > 0) {
                    setCustomMeasures(loadedMeasures);
                }

                const dataModelId = loadedConfig.dataModelId;

                if (dataModelId) {
                    apiFetch(`/datamodels/${dataModelId}`, token)
                        .then((dm: any) => {
                            setSelectedDataModel(dm);
                            if (dm?.current_refresh_status === 'RUNNING') {
                                window.dispatchEvent(new CustomEvent('datamodel-refresh-running', { detail: { modelId: dataModelId } }));
                            }
                        })
                        .catch(() => {
                            console.warn('[DashboardViewPage] DataModel fetch failed, using ID-only proxy:', dataModelId);
                            setSelectedDataModel({ id: dataModelId });
                        });

                    // Phase 1: fetch data source status for the badge
                    const pollDataStatus = async () => {
                        let done = false;
                        while (!done && !controller.signal.aborted) {
                            try {
                                const statusMap = await apiFetch(`/dashboards/data-status?model_ids=${dataModelId}`, token);
                                const info = statusMap[dataModelId];
                                if (info) {
                                    setDataSourceStatus(info);
                                    if (info.status === 'RUNNING' || info.status === 'REFRESHING') {
                                        window.dispatchEvent(new CustomEvent('datamodel-refresh-running', { detail: { modelId: dataModelId } }));
                                        // Wait 3s then poll again
                                        await new Promise<void>(r => setTimeout(r, 3000));
                                    } else {
                                        window.dispatchEvent(new CustomEvent('datamodel-refresh-completed', { detail: { modelId: dataModelId } }));
                                        done = true;
                                    }
                                } else {
                                    done = true;
                                }
                            } catch (e) {
                                done = true; // stop on error
                            }
                        }
                    };
                    pollDataStatus();
                }


                setLoading(false);

                // ── Record view metric (fire-and-forget, never blocks UI) ──
                // Guard: only fire once per dashboardId, regardless of how many
                // times the effect re-runs (refreshKey bumps, token changes, etc.).
                if (!shareToken && dashboardId && viewedDashboardRef.current !== dashboardId) {
                    viewedDashboardRef.current = dashboardId;
                    apiFetch(`/dashboards/${dashboardId}/viewed`, token, { method: 'POST' })
                        .catch(() => { /* non-critical — swallow silently */ });
                }

            } catch (err: any) {
                if (err.name === 'AbortError') return;
                console.error('Failed to load dashboard:', err);
                setError(err.message || 'Failed to load dashboard');
                setLoading(false);
            }
        };

        if (dashboardId) {
            loadDashboard();
        }

        return () => {
            controller.abort();
        };
    }, [dashboardId, token, shareToken, shareKeyVersion, refreshKey]);

    useEffect(() => {
        const loadShareCandidates = async () => {
            if (!isShareSheetOpen) return;
            if (!(user?.roleId && ['r1', 'r2', 'r3', 'r4', 'r7'].includes(user.roleId))) {
                setShareUserCandidates([]);
                return;
            }
            try {
                const endpoint = user?.tenantId ? `/users?tenant_id=${user.tenantId}` : '/users';
                const users = await apiFetch(endpoint, token, { method: 'GET' });
                const list = Array.isArray(users) ? users : [];
                const candidates = list
                    .filter((u: any) => String(u?.status || '').toLowerCase() === 'active')
                    .map((u: any) => ({ id: String(u.id), name: u.name, email: u.email }));
                setShareUserCandidates(candidates);
            } catch {
                setShareUserCandidates([]);
            }
        };
        loadShareCandidates();
    }, [isShareSheetOpen, token, user?.roleId, user?.tenantId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-slate-50 dark:bg-slate-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-slate-600 dark:text-slate-400">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-screen bg-slate-50 dark:bg-slate-900">
                <div className="text-center">
                    <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
                    <button
                        onClick={handleBack}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Back to Dashboards
                    </button>
                </div>
            </div>
        );
    }

    // Grid Config (Match Editor for Consistency)
    const colsMap = { lg: 120, md: 120, sm: 120, xs: 60, xxs: 40 };

    return (
        <div
            className={`flex flex-col bg-slate-50 dark:bg-slate-900 overflow-hidden font-sans text-slate-900 dark:text-slate-100 transition-all duration-300 ${isFullScreen ? 'fixed inset-0 z-[9999] h-screen w-screen' : 'h-full w-full relative'}`}
            onMouseMove={handleMouseMove}
        >
            {/* PDF export loading overlay */}
            {isExportingPdf && (
                <div className="absolute inset-0 z-[200] bg-white/70 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
                    <div className="flex flex-col items-center gap-3 px-5 py-4 rounded-xl bg-white shadow-md border border-slate-200">
                        <div className="w-7 h-7 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
                        <p className="text-sm font-medium text-slate-700">Preparing PDF — waiting for charts to render...</p>
                    </div>
                </div>
            )}

            {/* Embedded full-screen escape — shown ONLY when embedded and in full-screen,
                because both the studio header and the floating controls are hidden via !embedded */}
            {embedded && isFullScreen && (
                <div className="absolute top-3 right-3 z-[10000] pointer-events-auto">
                    <button
                        onClick={toggleFullScreen}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 dark:bg-slate-800/90 backdrop-blur-md shadow-lg border border-slate-200/60 dark:border-slate-700/50 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white text-xs font-semibold transition-all"
                        title="Exit Full Screen"
                    >
                        <Minimize className="w-3.5 h-3.5" />
                        <span>Exit Full Screen</span>
                    </button>
                </div>
            )}

            {/* Studio-only published-style view header */}
            {!embedded && studioFlow && (
                <div className="h-14 flex items-center px-4 gap-2 bg-[#f3f3f3] dark:bg-slate-800 border-b border-[#d6d6d6] dark:border-slate-700 z-30 relative shrink-0">
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                            type="button"
                            onClick={handleBack}
                            className="inline-flex items-center gap-1 px-2 py-1.5 hover:bg-[#e1dfdd] dark:hover:bg-slate-700 rounded text-[#605e5c] dark:text-slate-400 transition-colors text-sm font-medium"
                            title="Back"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                        <div className="w-px h-4 bg-[#d6d6d6] dark:bg-slate-600 mx-0.5" />
                        <Lock className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        <span className="text-sm font-semibold text-[#252423] dark:text-slate-100 truncate max-w-[260px]">
                            {dashboard?.name || 'Dashboard'}
                        </span>
                        {!!dashboard?.version_number && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-400 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-600 dark:text-emerald-300 leading-none flex-shrink-0 whitespace-nowrap">
                                v{dashboard.version_number}
                            </span>
                        )}
                        {(dashboard?.status || '').toLowerCase() === 'published' && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 whitespace-nowrap flex-shrink-0">
                                Published · View Only
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-1 ml-auto flex-shrink-0">
                        {/* Split Refresh button:
                             • Left (main) = fast reload from cached parquet (free, instant)
                             • Right (▾) = dropdown with "Refresh with Data Pull" (triggers backend job) */}
                        <div className="flex items-center rounded transition-all h-8">
                            {(() => {
                                const isBackendRefreshing = dataSourceStatus?.status === 'RUNNING' || selectedDataModel?.current_refresh_status === 'RUNNING';
                                const isEffectivelyPullingData = isDataPullRefreshing || isBackendRefreshing;
                                return (
                                    <>
                                        {/* Main: fast reload */}
                                        <button
                                            type="button"
                                            onClick={handleRefresh}
                                            disabled={isRefreshing || loading}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-l transition-all h-8 bg-transparent text-[#605e5c] dark:text-slate-400 hover:bg-[#e1dfdd] dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed border-r border-[#d6d6d6] dark:border-slate-600"
                                            title={isRefreshing ? 'Refreshing…' : 'Reload from cached data'}
                                        >
                                            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing || loading ? 'animate-spin' : ''}`} />
                                            <span>{isRefreshing ? 'Refreshing…' : 'Refresh'}</span>
                                        </button>
                                        {/* Dropdown: Refresh with Data Pull */}
                                        <ViewDropdownMenu>
                                            <ViewDropdownMenuTrigger asChild>
                                                <button
                                                    type="button"
                                                    disabled={isRefreshing || loading}
                                                    className="inline-flex items-center justify-center px-1.5 py-1.5 h-8 rounded-r bg-transparent text-[#605e5c] dark:text-slate-400 hover:bg-[#e1dfdd] dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                                    title="More refresh options"
                                                >
                                                    <ChevronDown className={`w-3 h-3 ${isEffectivelyPullingData ? 'animate-pulse text-amber-500' : ''}`} />
                                                </button>
                                            </ViewDropdownMenuTrigger>
                                            <ViewDropdownMenuContent align="end" className="w-52">
                                                <ViewDropdownMenuItem
                                                    disabled={isEffectivelyPullingData}
                                                    onClick={handleRefreshWithDataPull}
                                                >
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="font-medium">{isEffectivelyPullingData ? 'Refreshing full Data...' : 'Refresh with Data Pull'}</span>
                                                        <span className="text-[10px] text-gray-400">Fetches latest from source DB</span>
                                                    </div>
                                                </ViewDropdownMenuItem>
                                            </ViewDropdownMenuContent>
                                        </ViewDropdownMenu>
                                    </>
                                );
                            })()}
                        </div>
                        <button
                            type="button"
                            onClick={toggleFullScreen}
                            className="p-1.5 rounded transition-colors hover:bg-[#e1dfdd] dark:hover:bg-slate-700 text-[#605e5c] dark:text-slate-400"
                            title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
                        >
                            {isFullScreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                        </button>
                        <div className="w-px h-5 bg-[#d6d6d6] dark:bg-slate-600 mx-1" />
                        {/* Share — owners get full sheet + copy-link; anyone with a share link just gets copy */}
                        {isOwner && !shareToken ? (
                            <button
                                type="button"
                                onClick={() => setIsShareSheetOpen(true)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded transition-all h-8 bg-transparent text-[#605e5c] dark:text-slate-400 hover:bg-[#e1dfdd] dark:hover:bg-slate-700"
                                title="Share settings"
                            >
                                <Share2 className="w-3.5 h-3.5" />
                                <span>Share</span>
                            </button>
                        ) : shareToken ? (
                            <button
                                type="button"
                                onClick={() => {
                                    navigator.clipboard.writeText(window.location.href)
                                        .then(() => addNotification('Link copied to clipboard', 'success'))
                                        .catch(() => addNotification('Could not copy link', 'error'));
                                }}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded transition-all h-8 bg-transparent text-[#605e5c] dark:text-slate-400 hover:bg-[#e1dfdd] dark:hover:bg-slate-700"
                                title="Copy link"
                            >
                                <Copy className="w-3.5 h-3.5" />
                                <span>Copy Link</span>
                            </button>
                        ) : null}
                        {!shareToken && <div className="w-px h-5 bg-[#d6d6d6] dark:bg-slate-600 mx-1" />}
                        {canShowStudioHeaderEdit && (
                            <button
                                type="button"
                                onClick={handleEdit}
                                disabled={isUnlockingForEdit}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold bg-[#118DF0] hover:bg-[#0070c8] text-white transition-colors h-8 disabled:opacity-60 disabled:cursor-not-allowed"
                                title={isPublished ? 'Unlock dashboard for editing (draft from published)' : 'Edit Dashboard'}
                            >
                                <Pencil className="w-3.5 h-3.5" />
                                {isUnlockingForEdit ? 'Opening...' : 'Edit Dashboard'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Non-studio default header */}
            {!embedded && !studioFlow && (
                <div
                    className={`
                        absolute inset-x-0 top-0 z-40 pointer-events-none
                        transition-all duration-300 ease-in-out
                        ${isFullScreen ? (showControls ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1') : 'opacity-100'}
                    `}
                >
                    <div className="absolute top-3 left-3 pointer-events-auto">
                        <div className="flex items-center p-1 rounded-full bg-white/80 dark:bg-slate-900/80 backdrop-blur-md shadow-lg border border-slate-200/60 dark:border-slate-700/50">
                            <button
                                onClick={handleBack}
                                className="p-1.5 rounded-full text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/60 dark:hover:bg-slate-700/60 transition-colors"
                                title="Back to Dashboards"
                            >
                                <ArrowLeft className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div className="absolute top-3 right-3 pointer-events-auto flex items-center gap-2">
                        <button
                            onClick={handleRefresh}
                            disabled={isRefreshing || loading}
                            className="p-2 rounded-full bg-white/80 dark:bg-slate-900/80 backdrop-blur-md shadow-lg border border-slate-200/60 dark:border-slate-700/50 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            title="Refresh Dashboard"
                        >
                            <RefreshCw className={`w-4 h-4 ${isRefreshing || loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            type="button"
                            onClick={toggleFullScreen}
                            className="p-2 rounded-full bg-white/80 dark:bg-slate-900/80 backdrop-blur-md shadow-lg border border-slate-200/60 dark:border-slate-700/50 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 transition-all"
                            title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
                        >
                            {isFullScreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                        </button>
                        {canShowEditButton && (
                            <button
                                type="button"
                                onClick={handleEdit}
                                disabled={isUnlockingForEdit}
                                className="p-2 rounded-full bg-white/80 dark:bg-slate-900/80 backdrop-blur-md shadow-lg border border-slate-200/60 dark:border-slate-700/50 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                                title="Edit Dashboard"
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Data Source Banner — only shown when viewing platform sample data (not tenant's own dashboards) */}
            {dataSourceStatus?.source === 'platform' && isTenantScoped(user) && (
                <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-xs">
                    <Database className="w-3.5 h-3.5 shrink-0" />
                    <span>
                        <strong>Showing sample data.</strong>{' '}
                        Connect your own data source to see your organization's live data.
                    </span>
                </div>
            )}

            {/* Dashboard Grid */}
            <div
                id="dashboard-view-canvas"
                className="flex-1 overflow-auto"
                style={{
                    backgroundColor: dashboard?.theme_config?.canvas?.backgroundColor || colorTheme.canvasBg || 'transparent',
                    padding: dashboard?.theme_config?.canvas?.margin !== undefined
                        ? dashboard.theme_config.canvas.margin
                        : '16px', // Match edit mode default (16px, not 24px/p-6)
                    paddingTop: !embedded && !studioFlow
                        ? isFullScreen
                            ? '32px'
                            : '36px'
                        : undefined,
                    paddingBottom: '2rem'
                }}
            >
                <ResponsiveGridLayout
                    className="layout"
                    layouts={{ lg: dashboard?.layout_config || [], md: dashboard?.layout_config || [] }}
                    breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                    cols={colsMap}
                    rowHeight={5}
                    isDraggable={false}
                    isResizable={false}
                    margin={[5, 5]}
                    useCSSTransforms={true}
                >
                    {widgetItems.map((widget, widgetIndex) => {
                        const layoutStyles = getWidgetLayoutStyle(layoutId, widgetIndex, colorTheme);
                        return (
                            <div
                                key={widget.id}
                                data-grid={widget.layout}
                                className="overflow-hidden flex flex-col relative group"
                                style={layoutStyles.container}
                            >
                                {/* FLOATING ACTION OVERLAY (Top-Right) */}
                                {/* Always presence for layout stability, but content fades in/out */}
                                <div className={`
                                absolute top-2 right-2 z-50 flex items-center gap-1 
                                transition-all duration-200 ease-in-out
                                ${widget.id === activeInsightWidgetId ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0'}
                            `}>
                                    {/* AI Insights Trigger */}
                                    {widget.config?.aiInsights && (
                                        <div className="flex items-center gap-1 p-1 rounded-lg bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm shadow-sm border border-slate-200 dark:border-slate-700">
                                            <button
                                                className={`p-1.5 rounded-md transition-colors ${widget.id === activeInsightWidgetId ? 'text-amber-500 bg-amber-50 dark:bg-amber-900/30' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/30'}`}
                                                title="Toggle AI Insights"
                                                onClick={(e) => { e.stopPropagation(); setActiveInsightWidgetId(prev => prev === widget.id ? null : widget.id); }}
                                            >
                                                <Lightbulb className="w-3.5 h-3.5" fill={widget.id === activeInsightWidgetId ? "currentColor" : "none"} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {widget.config?.style?.showTitle !== false && (
                                    <div
                                        className="flex items-center justify-between px-3"
                                        style={{
                                            ...layoutStyles.header,
                                            height: `${widget.config?.style?.titleHeight ?? 32}px`,
                                            paddingLeft: `${widget.config?.style?.titlePaddingX ?? 12}px`,
                                            paddingRight: `${widget.config?.style?.titlePaddingX ?? 12}px`,
                                            ...(widget.config?.style?.backgroundColor ? {
                                                backgroundColor: widget.config.style.backgroundColor,
                                                borderBottom: '1px solid rgba(0,0,0,0.05)',
                                            } : {}),
                                        }}
                                    >
                                        <span
                                            className="truncate flex items-center gap-1.5 flex-1"
                                            style={{
                                                color: widget.config?.style?.color || (layoutStyles.showHeaderBg ? '#ffffff' : colorTheme.widgetText),
                                                fontSize: widget.config?.style?.fontSize ? `${widget.config.style.fontSize}px` : '10px',
                                                fontWeight: widget.config?.style?.fontWeight || '600',
                                                fontStyle: widget.config?.style?.fontStyle || 'normal',
                                            }}
                                        >
                                            {widget.config?.title || widget.title || `${widget.type} Widget`}
                                            {widget.config?.style?.showChartName && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-50 text-blue-500 border border-blue-100 shrink-0">
                                                    {CHART_TYPE_LABELS[widget.type] || widget.type}
                                                </span>
                                            )}
                                        </span>

                                        {/* Header Actions */}
                                        <div className="flexitems-center gap-1">
                                            <button
                                                className="p-1 rounded text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                                                title="Expand Widget"
                                                onClick={(e) => { e.stopPropagation(); setExpandedWidgetId(widget.id); }}
                                            >
                                                <Maximize2 className="w-3.5 h-3.5" />
                                            </button>

                                            {widget.config?.aiInsights && (
                                                <button
                                                    className={`p-1 rounded transition-colors ${widget.id === activeInsightWidgetId ? 'text-amber-500 bg-amber-50' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'}`}
                                                    title="Toggle AI Insights"
                                                    onClick={(e) => { e.stopPropagation(); setActiveInsightWidgetId(prev => prev === widget.id ? null : widget.id); }}
                                                >
                                                    <Lightbulb className="w-3.5 h-3.5" fill={widget.id === activeInsightWidgetId ? "currentColor" : "none"} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="flex-1 overflow-hidden relative">
                                    <WidgetRenderer
                                        widget={widget}
                                        dataset={widgetDatasets[widget.id]}
                                        dataModel={selectedDataModel}
                                        customMeasures={customMeasures}
                                        showInsights={widget.id === activeInsightWidgetId}
                                        onToggleInsights={() => setActiveInsightWidgetId(prev => prev === widget.id ? null : widget.id)}
                                        globalFilters={globalFilters}
                                        onFilterChange={handleFilterChange}
                                        onFiltersResolved={handleFiltersResolved}
                                        filtersReady={filtersReady}
                                        onUpdate={() => { }}
                                        isSnapshot={isExportSnapshot}
                                        dashboardTheme={colorThemeId}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </ResponsiveGridLayout>
            </div>

            {/* EXPANDED WIDGET MODAL */}
            {expandedWidgetId && (
                <div className="absolute inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm p-4 sm:p-8 flex flex-col">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl flex flex-col w-full h-full overflow-hidden border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-200">
                        {(() => {
                            const expandedWidget = widgetItems.find(w => w.id === expandedWidgetId);
                            if (!expandedWidget) return null;
                            return (
                                <>
                                    <div className="h-12 flex items-center justify-between px-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shrink-0">
                                        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 truncate">
                                            {expandedWidget.config?.title || expandedWidget.title || `${expandedWidget.type} Widget`}
                                        </h2>
                                        <div className="flex items-center gap-2">
                                            {expandedWidget.config?.aiInsights && (
                                                <button
                                                    className={`p-1.5 rounded-lg transition-colors ${expandedWidget.id === activeInsightWidgetId ? 'text-amber-500 bg-amber-50 dark:bg-amber-900/30' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-slate-800'}`}
                                                    title="Toggle AI Insights"
                                                    onClick={(e) => { e.stopPropagation(); setActiveInsightWidgetId(prev => prev === expandedWidget.id ? null : expandedWidget.id); }}
                                                >
                                                    <Lightbulb className="w-5 h-5" fill={expandedWidget.id === activeInsightWidgetId ? "currentColor" : "none"} />
                                                </button>
                                            )}
                                            <button
                                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                                onClick={() => setExpandedWidgetId(null)}
                                            >
                                                <X className="w-6 h-6" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex-1 overflow-hidden relative bg-white dark:bg-slate-800 p-2">
                                        <WidgetRenderer
                                            widget={expandedWidget}
                                            dataset={widgetDatasets[expandedWidget.id]}
                                            dataModel={selectedDataModel}
                                            customMeasures={customMeasures}
                                            showInsights={expandedWidget.id === activeInsightWidgetId}
                                            onToggleInsights={() => setActiveInsightWidgetId(prev => prev === expandedWidget.id ? null : expandedWidget.id)}
                                            globalFilters={globalFilters}
                                            onFilterChange={handleFilterChange}
                                            onFiltersResolved={handleFiltersResolved}
                                            filtersReady={filtersReady}
                                            onUpdate={() => { }}
                                        />
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
            <DashboardShareSheet
                open={isShareSheetOpen}
                dashboardId={dashboardId}
                dashboardTitle={dashboard?.name || 'Dashboard'}
                isPublished={(dashboard?.status || '').toLowerCase() === 'published'}
                canShareWithAll={!!(user?.roleId && ['r1', 'r2', 'r3', 'r4', 'r7'].includes(user.roleId))}
                canShareWithOrg={!!(user?.roleId && ['r1', 'r2', 'r3', 'r4'].includes(user.roleId))}
                sharedWith={sharedWith}
                publishShareMode={publishShareMode}
                availableUsers={shareUserCandidates}
                onClose={() => setIsShareSheetOpen(false)}
                onApplySettings={applyShareSettings}
                onCopyLink={async (link) => {
                    try {
                        await navigator.clipboard.writeText(link);
                        addNotification('Share link copied to clipboard', 'success');
                    } catch {
                        addNotification('Failed to copy link', 'error');
                    }
                }}
            />

        </div>
    );
};

export default DashboardViewPage;
